// @ts-nocheck
const init = (arr = []) => arr.slice(0, -1)
const last = (arr = []) => arr.slice(-1)[0]

/**
 * sequentialGroupBy groups a list of items based on the result of groupP
 *
 * A good example of this is for palettes, where the grouped things
 * will be a tuple of [ palette, [ ...group ] ]
 *
 * @param {any[]} items A list of items to be sequentially grouped
 * @param {Function} groupP a predicate function that returns `true' if you should create a new group
 * @param {Function} groupF a function to build the group object
 * @returns {any[[][]]} A list of tuples of [GroupCreator]
 */
export const sequentialGroupBy = (arr = [], groupP = (i) => i, groupF = (i) => i) => {
  return arr.reduce(
    (acc, entry, i, initArr) => {
      if (groupP(entry, i, initArr)) return [...acc, [groupF(entry), []]]

      const front = init(acc)
      const [group, items] = last(acc)

      return [...front, [group, items.concat(entry)]]
    },
    [[null, []]],
  )
}

export const unnest = (arr = [], unnestP = () => false) => {
  return arr
    .reduce(
      (acc, cur) => {
        const front = init(acc)
        const prev = last(acc) || []

        if (unnestP(cur)) return [...front, prev, cur]
        if (unnestP(prev)) return [...front, prev, [cur]]
        return [...front, [].concat(prev).concat(cur)]
      },
      [[]],
    )
    .filter((item) => !Array.isArray(item) || item.length > 0)
}

/**
 * @typedef {Function} ApplyPredicate
 * @param {Object} prevItem - The previous item in the list to test
 * @param {Object} currItem - The current item in the list to test
 * @returns {any} Returns a truthy value that can be passed into the `ApplyFunction`
 */

/**
 * @typedef {Function} ApplyFunction
 * @param {integer} i The index of the item being modified, i.e. 0 for first, 1 for second
 * @param {Object} item The item to be modified
 * @param {Object} [context] - Extra information to be passed
 * @returns {Object} Returns the modified `item`
 */

/**
 * applyToPairWhen applies a function to pairs within a list based on
 * a predicate
 *
 * A good example of this is for sibling spacing, where we look for
 * pairs of entries in a list of modules and modify them to include
 * some configured spacing
 *
 * @param {any[]} items A list of items to scan over and modify
 * @param {ApplyPredicate} applyP a predicate function that takes the current & previous entries in the list, returns `true' if you should apply `applyF` to them
 * @param {ApplyFunction} applyF a function to modify the entries, it is called with the index of the pair to modify, e.g. 0, 1
 * @returns {[any[],Boolean]} A tuple of the newly modified list of the original `items`, and a boolean of whether it was modified
 **/
export const applyToPairWhen = (items = [], applyP = () => false, applyF = (_) => _) => {
  let modded = false
  return [
    items.reduce((acc, currentModule, i) => {
      const frontModules = init(acc)
      const previousModule = last(acc) || {}

      const applyMatch = applyP(previousModule, currentModule, i)
      if (applyMatch) {
        modded = true

        /*
         * We have a match on the first module, when previous module
         * is undefined so we should only call applyF on the 1th,
         * i.e., currentModule which is the first module in `items`
         */
        if (i === 0) return frontModules.concat([applyF(1, currentModule, applyMatch)])

        return frontModules.concat([
          applyF(0, previousModule, applyMatch),
          applyF(1, currentModule, applyMatch),
        ])
      }

      return acc.concat(currentModule)
    }, []),
    modded,
  ]
}
